import isPlainObject from 'lodash.isplainobject'
import { ComponentType, LazyExoticComponent, lazy } from 'react'

import { getCookie } from '../app/shared/cookie'
import { CRYPTO_TICKERS } from './constants'
import { Instrument, InstrumentType } from './types'
import { RecordType } from './types'

/**
 * Function which wraps React.lazy and checks whether a chunk is preloaded
 * from async-manifest. If yes, kick in the promise resolve early so we can
 * instantly render the component
 */
export function lazyLoadComponent<Props>({
  chunkName,
  load,
}: {
  chunkName: string
  load: () => Promise<{ default: ComponentType<Props> }>
}): LazyExoticComponent<ComponentType<Props>> {
  // Resolve earlier if prefetch element is present
  const hasPreloadElement = document.querySelector(`[data-chunk-id=${chunkName}]`)

  if (hasPreloadElement) {
    const componentLoader = load()

    return lazy(() => componentLoader)
  }

  return lazy(load)
}

const isObject = (value: any): value is RecordType => isPlainObject(value)

/**
 * Parse text as JSON
 */
export function parseAsJSON<Shape>(value: unknown | Shape): Shape | undefined {
  if (isObject(value)) {
    return value as Shape
  }

  let parsedValue
  try {
    if (typeof value === 'string') {
      parsedValue = JSON.parse(value)
    }
  } catch (e) {
    return
  }
  return parsedValue
}

/**
 * remove @ symbol from a ticker
 */
export function cleanTicker(ticker: string) {
  return ticker.startsWith('@') ? ticker.substring(1) : ticker
}

/**
 * Get instrument for a ticker.
 *
 * - If ticker doesn’t start with `@` - stock
 * - Otherwise
 *   - one of `CRYPTO_TICKERS` - crypto
 *   - has length of 6 - forex
 *   - none of above - future
 *
 * NOTE: counterpart in charts 'charts/app/utils/chart.js'
 */

export function getInstrumentForTicker(ticker: string): InstrumentType {
  if (!ticker?.startsWith('@')) return Instrument.stock

  const cleanedTicker = cleanTicker(ticker)

  if (CRYPTO_TICKERS.includes(cleanedTicker.toUpperCase())) return Instrument.crypto
  if (cleanedTicker.length === 6) return Instrument.forex

  return Instrument.futures
}

/**
 * Async load Resize observer polyfill when we need it
 */
let resizeObserverPolyfill: typeof window.ResizeObserver
export async function loadResizeObserverPolyfill() {
  if (typeof window.ResizeObserver === 'undefined') {
    const polyfill = await import('resize-observer-polyfill')
    resizeObserverPolyfill = polyfill.default
    window.ResizeObserver = resizeObserverPolyfill
    return resizeObserverPolyfill as typeof window.ResizeObserver
  }
}

export async function getIsBrave() {
  return (navigator.brave && (await navigator.brave.isBrave())) || false
}

export function hasDarkModeFeature() {
  const ff = getCookie('featureFlags')
  return ff.includes('dark:1')
}

export function hasRedesignFeature() {
  const ff = getCookie('featureFlags')
  return ff.includes('redesign:1')
}

export function isRedesignFeatureEnabled() {
  return hasRedesignFeature() || hasDarkModeFeature()
}

export function isDarkModeFeatureEnabled() {
  const theme = getCookie('chartsTheme')
  return isRedesignFeatureEnabled() && theme.includes('dark')
}
